import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClock, FaLightbulb, FaBullseye, FaArrowUp, FaArrowDown, FaArrowRight, FaCheck, FaExclamation } from 'react-icons/fa';
import { SkeletonSearching } from '../../Skeleton';
import API_URL from '../../../utils/api';
import './CoachingModule.css';
export default function CoachingModule() {
  const navigate = useNavigate();
  const [userId] = useState(() => sessionStorage.getItem("userId") || localStorage.getItem("userId"));
  const [evaluationData, setEvaluationData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchEvaluationData = async () => {
      try {
        // Fetch user's evaluation history
        const response = await fetch(`${API_URL}/api/evaluations/user/${userId}`);
        const data = await response.json();
        setEvaluationData(data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchEvaluationData();
  }, [userId]);
  const handleContinue = () => {
    navigate('/dashboard');
  };
  if (loading) {
    return (
      <div className="coaching-root">
        <SkeletonSearching />
      </div>
    );
  }
  if (!evaluationData || evaluationData.paired.length === 0) {
    return (
      <div className="coaching-root">
        <div className="coaching-error">
          <p>No evaluation data available yet.</p>
          <button onClick={handleContinue} className="coaching-continue-btn">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }
  // Get the most recent evaluation (current session)
  const currentEval = evaluationData.paired[evaluationData.paired.length - 1];
  const averageDiscrepancy = currentEval.averageDiscrepancy;
  const accuracy = Math.max(0, 10 - averageDiscrepancy); // Scale of 0-10
  // Determine accuracy level
  let accuracyLevel = 'Excellent';
  let accuracyColor = '#4caf50';
  let feedbackMessage = '';
  if (averageDiscrepancy <= 1) {
    accuracyLevel = 'Excellent';
    accuracyColor = '#4caf50';
    feedbackMessage = 'Outstanding! Your predictions were remarkably accurate. You have a strong understanding of how social interactions will make you feel.';
  } else if (averageDiscrepancy <= 2) {
    accuracyLevel = 'Good';
    accuracyColor = '#8bc34a';
    feedbackMessage = 'Great job! Your predictions were quite accurate. You have good insight into your emotional responses to social situations.';
  } else if (averageDiscrepancy <= 3) {
    accuracyLevel = 'Moderate';
    accuracyColor = '#ffc107';
    feedbackMessage = 'Not bad! There\'s some room for improvement in predicting your emotional responses. Keep practicing!';
  } else {
    accuracyLevel = 'Needs Improvement';
    accuracyColor = '#ff9800';
    feedbackMessage = 'Your predictions differed notably from reality. This is common! Most people struggle with affective forecasting. The good news: it improves with practice.';
  }
  // Calculate improvement if there are multiple sessions
  let improvementText = '';
  if (evaluationData.paired.length > 1) {
    const previousEval = evaluationData.paired[evaluationData.paired.length - 2];
    const previousDiscrepancy = previousEval.averageDiscrepancy;
    const improvement = previousDiscrepancy - averageDiscrepancy;
    if (improvement > 0.5) {
      improvementText = <>Great progress! Your accuracy improved by {improvement.toFixed(1)} points since your last session.</>;
    } else if (improvement < -0.5) {
      improvementText = <>Your accuracy decreased by {Math.abs(improvement).toFixed(1)} points. Don't worry - consistency comes with practice!</>;
    } else {
      improvementText = <>Your accuracy remained consistent with your previous session.</>;
    }
  }
  // Detailed breakdown
  const breakdown = [];
  const labels = {
    anticipatedEnjoyment: 'Enjoyment',
    anticipatedConnection: 'Connection',
    anticipatedComfort: 'Comfort',
    anticipatedInterest: 'Interest',
    anticipatedOpenness: 'Openness'
  };
  for (const key in currentEval.discrepancies) {
    breakdown.push({
      label: labels[key] || key,
      discrepancy: currentEval.discrepancies[key],
      accurate: currentEval.discrepancies[key] <= 2
    });
  }
  return (
    <div className="coaching-root">
      {/* Header */}
      <div className="coaching-header">
        <h1><FaBullseye /> Your Affective Forecasting Report</h1>
        <p className="coaching-subtitle">How well did you predict your emotional experience?</p>
      </div>
      {/* Overall Accuracy Card */}
      <div className="accuracy-card" style={{ borderColor: accuracyColor }}>
        <div className="accuracy-label">Overall Accuracy</div>
        <div className="accuracy-score" style={{ color: accuracyColor }}>
          {accuracy.toFixed(1)}/10
        </div>
        <div className="accuracy-level" style={{ color: accuracyColor }}>
          {accuracyLevel}
        </div>
        <div className="accuracy-bar-container">
          <div 
            className="accuracy-bar-fill" 
            style={{ 
              width: `${(accuracy / 10) * 100}%`,
              backgroundColor: accuracyColor 
            }}
          />
        </div>
      </div>
      {/* Feedback Message */}
      <div className="feedback-card">
        <div className="feedback-icon"><FaLightbulb size={32} /></div>
        <p className="feedback-text">{feedbackMessage}</p>
      </div>
      {/* Improvement Tracker */}
      {improvementText && (
        <div className="improvement-card">
          <p className="improvement-text">{improvementText}</p>
        </div>
      )}
      {/* Detailed Breakdown */}
      <div className="breakdown-section">
        <h3>Detailed Breakdown</h3>
        <div className="breakdown-grid">
          {breakdown.map((item, index) => (
            <div key={index} className="breakdown-item">
              <div className="breakdown-label">{item.label}</div>
              <div className="breakdown-discrepancy">
                {item.accurate ? <FaCheck /> : <FaExclamation />} {item.discrepancy.toFixed(1)} point{item.discrepancy !== 1 ? 's' : ''} off
              </div>
              <div className={`breakdown-status ${item.accurate ? 'accurate' : 'needs-work'}`}>
                {item.accurate ? 'Accurate' : 'Needs Work'}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Insights & Tips */}
      <div className="insights-section">
        <h3><FaLightbulb /> Key Insights</h3>
        <div className="insights-content">
          <div className="insight-item">
            <strong>Affective Forecasting Bias:</strong> Research shows people often <em>underestimate</em> how much they'll enjoy deep conversations with strangers.
          </div>
          <div className="insight-item">
            <strong>The Power of Connection:</strong> Studies find that meaningful conversations create stronger bonds than we predict.
          </div>
          <div className="insight-item">
            <strong>Practice Makes Perfect:</strong> Your forecasting accuracy improves with each session as you learn your emotional patterns.
          </div>
        </div>
      </div>
      {/* Session Stats */}
      <div className="stats-section">
        <div className="stat-box">
          <div className="stat-number">{evaluationData.totalSessions}</div>
          <div className="stat-label">Total Sessions</div>
        </div>
        <div className="stat-box">
          <div className="stat-number">{evaluationData.averageAccuracy.toFixed(1)}/10</div>
          <div className="stat-label">Average Accuracy</div>
        </div>
        <div className="stat-box">
          <div className="stat-number">
            {accuracy > evaluationData.averageAccuracy ? <FaArrowUp style={{color: '#4CAF50'}} /> : 
             accuracy < evaluationData.averageAccuracy ? <FaArrowDown style={{color: '#f44336'}} /> : 
             <FaArrowRight style={{color: '#999'}} />}
          </div>
          <div className="stat-label">Trend</div>
        </div>
      </div>
      {/* Continue Button */}
      <div className="coaching-actions">
        <button onClick={handleContinue} className="coaching-continue-btn">
          Complete Session →
        </button>
        <p className="coaching-note">
          Keep practicing to improve your emotional forecasting skills!
        </p>
      </div>
    </div>
  );
}
